import { useCallback, useEffect, useRef, useState } from "react";
import { coachApi } from "@/api";
import type { InviteCode } from "@/lib/types";
import { useUI } from "@/lib/theme";

function usesLabel(code: InviteCode, t: (k: string) => string): string {
  if (code.max_uses === null) {
    return t("link_codes_uses_unlimited").replace("{uses}", String(code.uses));
  }
  return t("link_codes_uses")
    .replace("{uses}", String(code.uses))
    .replace("{max}", String(code.max_uses));
}

function CopyButton({ code, t }: { code: string; t: (k: string) => string }) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  function copy() {
    void navigator.clipboard.writeText(code).then(() => {
      setCopied(true);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => setCopied(false), 1800);
    });
  }

  return (
    <button
      onClick={copy}
      className="rounded-md border border-line px-2 py-0.5 text-xs text-muted transition hover:border-accent hover:text-accent"
    >
      {copied ? t("link_codes_copied") : t("link_codes_copy")}
    </button>
  );
}

export default function CoachLinkCodes() {
  const { t, lang } = useUI();
  const [codes, setCodes] = useState<InviteCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const load = useCallback(() => {
    coachApi.linkCodes().then((r) => {
      setCodes(r.codes);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  async function create() {
    setCreating(true);
    try {
      const res = await coachApi.createLinkCode({});
      setCodes((prev) => [res.code, ...prev]);
    } finally {
      setCreating(false);
    }
  }

  async function toggleStatus(code: InviteCode) {
    const next = code.status === "active" ? "disabled" : "active";
    await coachApi.setLinkCodeStatus(code.code, next);
    setCodes((prev) => prev.map((c) => (c.code === code.code ? { ...c, status: next } : c)));
  }

  return (
    <section className="rounded-xl border border-line bg-panel/70 p-4">
      <h2 className="mb-1 text-sm font-semibold uppercase tracking-wider text-muted">
        {t("link_codes_title")}
      </h2>
      <p className="mb-4 text-xs text-muted">
        {lang === "es"
          ? "Cada código es de un solo uso y caduca en 7 días. El prefijo se genera automáticamente con tu nombre."
          : "Each code is single-use and expires in 7 days. The prefix is derived automatically from your name."}
      </p>

      <div className="mb-4">
        <button
          onClick={() => void create()}
          disabled={creating}
          className="h-8 rounded-lg bg-accent px-4 text-sm font-semibold text-white transition enabled:hover:brightness-110 disabled:opacity-40"
        >
          {t("link_codes_new")}
        </button>
      </div>

      {loading && <p className="text-sm text-muted">{t("admin_loading")}</p>}
      {!loading && codes.length === 0 && (
        <p className="text-sm text-muted">{t("link_codes_empty")}</p>
      )}

      <ul className="space-y-2">
        {codes.map((code) => (
          <li
            key={code.code}
            className={`flex flex-wrap items-center gap-2 rounded-xl border border-line px-4 py-3 ${
              code.status === "disabled" ? "opacity-50" : ""
            }`}
          >
            <span className="font-mono text-sm font-semibold text-fg">{code.code}</span>
            <CopyButton code={code.code} t={t} />
            <span className="text-xs text-muted">{usesLabel(code, t)}</span>
            {code.expires_at && (
              <span className="text-xs text-muted">exp {code.expires_at.slice(0, 10)}</span>
            )}
            {code.redeemed_user_id && (
              <LinkedAthlete
                name={[code.redeemed_user_firstname, code.redeemed_user_lastname].filter(Boolean).join(" ") || code.redeemed_user_id.slice(0, 8)}
                profilePic={code.redeemed_user_profile_pic ?? null}
              />
            )}
            <button
              onClick={() => void toggleStatus(code)}
              className="ml-auto rounded-md border border-line px-2 py-0.5 text-xs text-muted transition hover:border-accent hover:text-accent"
            >
              {code.status === "active" ? t("link_codes_disable") : t("link_codes_enable")}
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

function LinkedAthlete({ name, profilePic }: { name: string; profilePic: string | null }) {
  return (
    <span className="flex items-center gap-2 rounded-full bg-panel2/60 px-2 py-1 text-xs text-fg">
      {profilePic ? (
        <img src={profilePic} alt="" className="h-5 w-5 rounded-full object-cover" />
      ) : (
        <span className="flex h-5 w-5 items-center justify-center rounded-full bg-accent/15 text-[10px] font-semibold text-accent">
          {name.slice(0, 1).toUpperCase()}
        </span>
      )}
      <span>{name}</span>
    </span>
  );
}
